import { useCallback, useEffect, useRef, useState } from 'react';
import useFocus from '@/hooks/useFocus';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useProfile from '@/hooks/useProfileV2';
import { filterByWord } from '@/utils/global';
import AssignedUsersComponent, {
  AssignesUsersComponentProps,
  inputId,
  resultDropdownId,
} from './AssignedUsers.component';
import { UserType } from '../UserSuggestions/UserSuggestions.component';

export type AssignesUsersProps = Omit<
  AssignesUsersComponentProps,
  | 'searchResults'
  | 'onSelectItem'
  | 'onChangeHandler'
  | 'inputRef'
  | 'resultDropdownRef'
  | 'isDropdownVisible'
> & {
  onAssign: (id: string) => void;
  allReviewers: UserType[];
  onSearch?: (value: string) => void;
};

const AssignedUsers = ({
  allReviewers,
  onAssign,
  value: searchValue,
  onSearch,
  ...componentProps
}: AssignesUsersProps): JSX.Element => {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const resultDropdownRef = useRef<HTMLDivElement>(null);
  const focus = useFocus(inputRef);
  const [isDropdownVisible, setDropdownVisible] = useState<boolean>(true);
  const { isAdmin } = useProfile();

  const filteredItems = search
    ? (allReviewers || []).filter((option) => {
        return (
          filterByWord(option.firstName, search) || filterByWord(option?.lastName ?? '', search)
        );
      })
    : [];

  useEffect(() => {
    if (focus) setDropdownVisible(true);
  }, [focus]);

  const hideDropdown = useCallback((event: any) => {
    const eventPath = event.composedPath() ?? [];
    const componentIDs = [inputId, resultDropdownId];
    const checkPathId = (path): boolean => componentIDs.includes(path.id);
    const hasClickedInsideComponent = eventPath.some(checkPathId);

    if (!hasClickedInsideComponent) {
      setDropdownVisible(false);
    }
  }, []);

  useOnClickOutside(resultDropdownRef, hideDropdown);

  const onChangeHandler = (event: any): void => {
    const {
      target: { value },
    } = event;

    onSearch?.(value);
    setSearch(value);
  };

  const onSelectItem = (id: string): void => {
    onAssign(id);

    onSearch?.('');
    setSearch('');
  };

  return (
    <AssignedUsersComponent
      shouldShowRemoveButton={isAdmin}
      searchResults={filteredItems}
      value={searchValue ?? search}
      onChangeHandler={onChangeHandler}
      onSelectItem={onSelectItem}
      inputRef={inputRef}
      resultDropdownRef={resultDropdownRef}
      isDropdownVisible={isDropdownVisible}
      {...componentProps}
    />
  );
};

export default AssignedUsers;
